import {
  FormSubmitError,
  fallbackErrorMessage,
  reportSubmitFailure,
} from '../shared/configurable/mock-submit';
import { getRecaptchaClient } from '../../lib/recaptcha';
import type { NewsletterFormValues } from './useNewsletterForm';

type NewsletterSubmitOptions = {
  pageUrl: string;
  recaptchaAction: string;
  recaptchaSiteKey?: string;
  source?: string;
  url: string;
  values: NewsletterFormValues;
};

const REPORT_VARIANT = 'newsletter';
const NETWORK_ERROR_MESSAGE =
  'No pudimos conectar con el servidor. Revisa tu conexión a internet e intenta nuevamente.';
const BODY_SNIPPET_LENGTH = 300;

async function getRecaptchaToken(siteKey: string, action: string): Promise<string> {
  const grecaptcha = await getRecaptchaClient(siteKey);
  if (!grecaptcha) throw new Error('reCAPTCHA client unavailable');

  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
    });
  });
}

type SubscribeResponseBody = {
  success?: boolean;
  status?: boolean;
  message?: string;
};

function parseResponseBody(rawBody: string): SubscribeResponseBody | null {
  try {
    const parsed = JSON.parse(rawBody) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as SubscribeResponseBody) : null;
  } catch {
    return null;
  }
}

async function readResponseBody(response: Response): Promise<string> {
  if (typeof response.text !== 'function') return '';
  return response.text().catch(() => '');
}

function isRejected(body: SubscribeResponseBody | null): boolean {
  return body?.success === false || body?.status === false;
}

export async function submitNewsletter({
  pageUrl,
  recaptchaAction,
  recaptchaSiteKey,
  source,
  url,
  values,
}: NewsletterSubmitOptions): Promise<void> {
  const token = recaptchaSiteKey
    ? await getRecaptchaToken(recaptchaSiteKey, recaptchaAction)
    : undefined;

  const payload = {
    ...values,
    page_url: pageUrl,
    recaptcha_response: token,
    source,
  };
  const reportContext = { variant: REPORT_VARIANT, source, pageUrl };

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    reportSubmitFailure(reportContext, url, {
      kind: 'network',
      status: null,
      bodySnippet: null,
      errorMessage: String(error),
    });
    throw new FormSubmitError(NETWORK_ERROR_MESSAGE, {
      success: false,
      message: NETWORK_ERROR_MESSAGE,
    });
  }

  const rawBody = await readResponseBody(response);
  const body = parseResponseBody(rawBody);

  if (response.ok && !isRejected(body)) return;

  const bodySnippet = rawBody.slice(0, BODY_SNIPPET_LENGTH) || null;
  if (body === null) {
    reportSubmitFailure(reportContext, url, {
      kind: 'non-json',
      status: response.status,
      bodySnippet,
      errorMessage: null,
    });
  } else if (response.status >= 500) {
    reportSubmitFailure(reportContext, url, {
      kind: 'server-error',
      status: response.status,
      bodySnippet,
      errorMessage: null,
    });
  }

  const message = body?.message || fallbackErrorMessage(response.status);
  throw new FormSubmitError(message, { success: false, message });
}
